/**
 * @file   src\Pages\AddCampaign.tsx
 * @brief  Add CCampaign page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
    React,
    useState,
    useEffect,
    Container,
    Button,
    Breadcrumb,
    Row,
    Col,
    useOutletContext,
    useNavigate,
    useParams,
    Modal
} from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWTextarea from '../components/CWTextarea';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { MessageToaster } from '../utils/toastUtil';
import { DISCOUNT_MAX_LENGTH, PROMO_CODE_MAX_LENGTH, PROMOTION_DETAIL_MAX_LENGTH, PROMOTION_NAME_MAX_LENGTH } from '../utils/constants';
import { AppContextType } from '../interfaces/generalInterface';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DiscountType, userType } from '../utils/enums';
import { CampaignApi, CompanyApi, LocationApi, SubscriptionApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import CWInputGroup from '../components/CWInputGroup';
import SmallLoader from '../components/SmallLoader';
import { CampaignItem } from '../interfaces/promotionInterface';
import CWRadio from '../components/CWRadio';
import { addCampaignSchema } from '../validations/addCampaignSchema';
import moment from "moment-timezone"
import { CommonMessages } from '../messages/validationMessages';
import CWCheckbox from '../components/CWCheckbox';

const AddCampaign = (props: any) => {

    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false;
    const bayType = 0 // inbay/tunnel
    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });
    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);

    const [userSubscriptions, setuserSubscriptions] = useState<Option[]>([]);
    const [washNames, setwashNames] = useState<Option[]>([]);


    // Convert ConnectivityType enum to an array of options
    const discountOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(DiscountType).filter(key => !isNaN(Number(key))).map(key => ({
            label: DiscountType[key as keyof typeof DiscountType],
            value: Number(key)
        }))
    ];
    const SubscriptionTypeOptions = [
        { label: 'Select', value: 0 },
        { label: 'Single wash', value: 1 },
        { label: 'Subscription', value: 2 },
    ]

    const durationOpions = [
        { label: 'Select', value: '0' },
        { label: 'One Month', value: '1' },
        { label: 'Two Months', value: '2' },
        { label: 'Three Months', value: '3' },
        { label: 'Four Months', value: '4' },
        { label: 'Five Months', value: '5' },
        { label: 'Six Months', value: '6' },
        { label: 'Seven Months', value: '7' },
        { label: 'Eight Months', value: '8' },
        { label: 'Nine Months', value: '9' },
        { label: 'Ten Months', value: '10' },
        { label: 'Eleven Months', value: '11' },
        { label: 'Twelve Months', value: '12' },
    ];



    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const params = useParams();


    // Initialize states for formdata
    const [formData, setFormData] = useState<CampaignItem>({
        assignedlocation: null,
        washname: null,
        campaignname: '',
        generatecode: '',
        discounttype: null,
        discount_value: null,
        validFrom: '',
        validTo: '',
        campaigndetails: '',
        subscriptionType: null,
        userType: 1,
        CampaignDuration: '',
        userSubscription: null,
        multipleCodeGeneration:false,
        noOfCodes:0

    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        assignedlocation: '',
        washname: '',
        campaignname: '',
        generatecode: '',
        discounttype: '',
        discount_value: '',
        validFrom: '',
        validTo: '',
        campaigndetails: '',
        CampaignDuration: '',
        userSubscription: '',
        subscriptionType: '',
        multipleCodeGeneration:'',
        noOfCodes:''

    });

    const [loading, setLoading] = useState(true);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const toaster = new MessageToaster();

    const [isSignedUpUser, setIsSignedUpUser] = useState(true);



    useEffect(() => {
        getCompanyAddress()
        getCompany()
        // To get details by id
        if (props.edit) {
            getCampaignDetails()
        }
    }, [])


    // To display heading
    useEffect(() => {
        handleUpdate()
    }, [company])

    // Fetch washname options based on assigned location
    useEffect(() => {
        if (formData.assignedlocation) {

            getWashnames()
        }
    }, [formData.assignedlocation]);  // Fetch wash names when assigned location changes

    useEffect(() => {
        if (formData.assignedlocation && formData.subscriptionType) {

            getSubscriptions()
        }
    }, [formData.assignedlocation, formData.subscriptionType]);

    // Function to handle radio button change
    const handleUserTypeChange = (isSignedUser: boolean) => {
        setIsSignedUpUser(isSignedUser);
        setFormData((prevState) => ({
            ...prevState,
            userType: isSignedUser ? 1 : 0, // 1 for SignedUp Users, 0 for Unsigned Users
            userSubscription: isSignedUser ? prevState.userSubscription : null, // Reset userSubscription if Unsigned
        }));
    };
    // To get company details
    const getCompanyAddress = async () => {
        try {
            const id = params.id ? parseInt(params.id) : 0;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                setCompany(data.data);
            }
            setLoading(false)
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${params.id}`;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY + param1) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);

            // To get locations under company
            await getAssignedLocations(data.data.carWashGUID)

        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    // Function get subscriptions in different locations
    const getWashnames = async () => {
        try {
            // if(!carwash_owner)
            //     {
            const locationIds = [{ location_id: formData.assignedlocation }]
            const locationIdsString = encodeURIComponent(JSON.stringify(locationIds));
            const param = `?pageNo=1&locations=${locationIdsString}&bayType=${bayType}&sortOrder=1&sortField=1`;

            const { data } = await axios.get(SubscriptionApi.CARWASH_OWNER_LIST_SUBSCRIPTIONS + param)
            // const { data } = await axios.get(SubscriptionApi.CARWASH_OWNER_GET_SUBSCRIPTION_LIST_BY_LOCATION + formData.assignedlocation)
            if (data?.error === 0) {
                const wash_list: any[] = data?.data?.list

                if (wash_list.length > 0) {
                    const transformedArray: Option[] = [
                        ...wash_list.map(item => ({
                            value: item.SubscriptionID,
                            label: item.WashName
                        }))
                    ];

                    setuserSubscriptions(transformedArray);
                }
                else {
                    setuserSubscriptions([])
                }
            } else {
                setuserSubscriptions([])
            }
            // }
        } catch (error: any) {
            console.error('Error fetching wash names:', error);
            throw new Error(error.response?.data?.message || error.message);

        }
    };


    // Function get subscriptions in different locations
    const getSubscriptions = async () => {
        try {
            // if(!carwash_owner)
            //     {
            const locationIds = [{ location_id: formData.assignedlocation }]
            const locationIdsString = encodeURIComponent(JSON.stringify(locationIds));
            const param = `?subscriptionType=${formData.subscriptionType == 1 ? "day" : "month"}&pageNo=1&locations=${locationIdsString}&bayType=${bayType}&sortOrder=1&sortField=1`;

            const { data } = await axios.get(SubscriptionApi.CARWASH_OWNER_LIST_SUBSCRIPTIONS + param)
            if (data?.error === 0) {
                const wash_list: any[] = data?.data?.list

                if (wash_list.length > 0) {
                    const transformedArray: Option[] = [
                        ...wash_list.map(item => ({
                            value: item.SubscriptionID,
                            label: item.WashName
                        }))
                    ];
                    setwashNames(transformedArray);
                }
                else {
                    setwashNames([])
                }
            } else {
                setwashNames([])
            }
            // }
        } catch (error: any) {
            console.error('Error fetching wash names:', error);
            throw new Error(error.response?.data?.message || error.message);

        }
    };

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: props.edit ? "Edit Campaign" : "Add New Campaign", url: `/${pageURLs.manage_campaigns}`, showBackButton: true, showMainMenu: true, activeLink: "" });
    };

    // Function to get Locations
    const getAssignedLocations = async (carwashGuid: string) => {
        try {

            const { data } = (!carwash_owner) ?
                await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid)
                : await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setLocations(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getCampaignDetails = async () => {
        try {


            const { data } = await axios.get(CampaignApi.GET_CAMPAIGN_DETAILS_BY_ID + params.campaignId);
            if (data.error === 0) {
                const campaign = data.data
                const campaignStartDate = moment(campaign.validFrom).toDate();
                const campaignEndDate = moment(campaign.validTo).toDate();
                setFormData({
                    assignedlocation: campaign.locationId,
                    washname: campaign.subscriptionID,
                    campaignname: campaign.campaignName,
                    generatecode: campaign.CampaignCode,
                    discounttype: campaign.discountType,
                    discount_value: campaign.discountValue,
                    validFrom: moment(campaignStartDate).format('MM/DD/YYYY'),
                    validTo: moment(campaignEndDate).format('MM/DD/YYYY'),
                    campaigndetails: campaign.CampaignDetails,
                    userSubscription: campaign.userSubscription,
                    CampaignDuration: campaign.duration,
                    subscriptionType: campaign.subscriptionType,
                    userType: campaign.userType,
                    multipleCodeGeneration:campaign.hasMultipleCode,
                    noOfCodes:campaign.noOfCodes
                });
                setStartDate(campaignStartDate);
                setEndDate(campaignEndDate);
                setIsSignedUpUser(campaign.userType);

            }
            else {
                // show error message
                toaster.toastError(data.message);
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Triggers when input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'discount_value') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^\d{0,8}(\.\d{0,2})?$/;

            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: parseInt(value),
                });
            }
        }
        else if(name === 'noOfCodes'){
            const regex = /^\d{0,8}(\.\d{0,4})?$/;

            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: parseInt(value),
                });
            }
        }
        
         else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    }

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';
        const isSingleWash = name === "subscriptionType" && value === 1;
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name) || isSingleWash) {
            setErrors({
                ...errors,
                [name]: "",
                ...(isSingleWash ? { CampaignDuration: "" } : {}),
            })
        }


        
        if (name == "userSubscription" && !props.edit) {
            setFormData({
                ...formData,
                userSubscription: value,
                washname: null,
                subscriptionType: null,
            });

        }
        else if (isSingleWash) {
            // Duration is read-only for single wash, so persist the displayed One Month value.
            setFormData({
                ...formData,
                subscriptionType: value,
                CampaignDuration: "1",
            });
        }
        else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }


    };

    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>, name: string) => (date: Date | null) => {
        setter(date);
        const updatedFormData = {
            ...formData,
            [name]: date,
        };

        const parseDateValue = (value: unknown): Date | null => {
            if (!value) return null;
            if (value instanceof Date && !isNaN(value.getTime())) return value;
            const parsedDate = new Date(value as string);
            return isNaN(parsedDate.getTime()) ? null : parsedDate;
        };

        const fromDate = parseDateValue(updatedFormData.validFrom);
        const toDate = parseDateValue(updatedFormData.validTo);

        setErrors((prevErrors) => {
            const updatedErrors = {
                ...prevErrors,
                [name]: ""
            };

            if (fromDate && toDate && toDate < fromDate) {
                updatedErrors.validTo = CommonMessages.not_less_than;
            } else if (updatedErrors.validTo === CommonMessages.not_less_than) {
                updatedErrors.validTo = "";
            }

            return updatedErrors;
        });

        setFormData(updatedFormData);
    };

    // Triggers on submit
    const handleSubmit = async () => {
        // event.preventDefault()
        try {
            // Validate form data
            await addCampaignSchema.validate(formData, { abortEarly: false });

            try {
                const locale = 'en-us';
                setLoading(true)
                const fromDate = new Date(formData.validFrom,).toLocaleDateString(locale);
                const toDate = new Date(formData.validTo,).toLocaleDateString(locale);
                const fromDateArray = fromDate.split('/');
                const toDateArray = toDate.split('/')
                // In case of edit
                if (props.edit) {
                    const updatedformData = {
                        locationId: formData.assignedlocation,
                        campaignId: params.campaignId ? parseInt(params.campaignId) : 0,
                        campaignName: formData.campaignname,
                        validFrom: fromDateArray[2] + '-' + fromDateArray[0] + '-' + fromDateArray[1],
                        validTo: toDateArray[2] + '-' + toDateArray[0] + '-' + toDateArray[1],
                        campaignDetails: formData.campaigndetails,
                        // duration: formData.CampaignDuration ? parseInt(formData.CampaignDuration) : 0
                    }
                    const { data } = await axios.post(CampaignApi.EDIT_CAMPAIGN, updatedformData);
                    if (data?.error === 0) {
                        toaster.toastSuccess(data?.message);
                        navigate(`/${pageURLs.manage_campaigns}`)

                    }
                    else {
                        // show error message
                        toaster.toastError(data?.message);
                    }
                } else {
                    const newformData = {
                        locationId: formData.assignedlocation,
                        subscriptionId: formData.washname,
                        userSubscription: formData.userSubscription,
                        campaignName: formData.campaignname,
                        campaignCode: formData.generatecode,
                        discountType: formData.discounttype,
                        discount: formData.discount_value,
                        validFrom: fromDateArray[2] + '-' + fromDateArray[0] + '-' + fromDateArray[1],
                        validTo: toDateArray[2] + '-' + toDateArray[0] + '-' + toDateArray[1],
                        campaignDetails: formData.campaigndetails,
                        userType: formData.userType,
                        duration: formData.CampaignDuration ? parseInt(formData.CampaignDuration) : 0,
                        hasMultipleCode: formData.multipleCodeGeneration,
                        noOfCodes: formData.noOfCodes
                    }

                    const { data } = await axios.post(CampaignApi.CREATE_CAMPAIGNS, newformData);
                    if (data?.error === 0) {
                        toaster.toastSuccess(data?.message)
                        navigate(`/${pageURLs.manage_campaigns}`)

                    }
                    else {
                        // show error message
                        toaster.toastError(data?.message);
                    }

                }

            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    const [showReviewpopup, setShowReviewpopup] = useState<boolean>(false);

    const hideReviewpopupModal = () => {
        setShowReviewpopup(false);
    }
    const showReviewpopupModal = async () => {
        try {
            // Validate form data
            await addCampaignSchema.validate(formData, { abortEarly: false });

            setShowReviewpopup(true);
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    const generateCode = () => {
        //const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const characters = '0123456789';
        let promoCode = '';
        for (let i = 0; i < PROMO_CODE_MAX_LENGTH; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            promoCode += characters[randomIndex];
        }

        // update formData with generated code
        setFormData(prevState => ({
            ...prevState,
            generatecode: promoCode,
        }));
    }

    const handleMultipleCodeCheck = (multiple:boolean) => {
        setFormData(prevState => ({
            ...prevState,
            multipleCodeGeneration: multiple,
        }));
        
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                {props.userType !== userType["CARWASH-OWNER"] ?
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => {
                            
                                if (props.userType !== userType["CARWASH-OWNER"]) {
                                    itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                                } else {
                                    itemClick(pageURLs.manage_carwash);
                                }
                            
                        }}>Manage Car Washes</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={() => {
                            if (props.userType !== userType["CARWASH-OWNER"]) {
                                itemClick(`/superadmin/${pageURLs.carwash_locations}/${params.id}`);
                            } else {
                                itemClick(`${pageURLs.carwash_locations}/1`);
                            }
                        }}  > {company.companyName}</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={() => {
                            if (props.userType !== userType["CARWASH-OWNER"]) {
                                itemClick(`/superadmin/${pageURLs.manage_campaigns}/${params.id}`);
                            } else {
                                itemClick(pageURLs.manage_campaigns);
                            }
                        }}>Manage Promotions</Breadcrumb.Item>
                        <Breadcrumb.Item active>{props.edit ? "Edit Campaign" : "Add New Campaign"}</Breadcrumb.Item>
                    </Breadcrumb>
                    : null}


                <div className='cw-form-outer-wrap'>
                    {/* <form onSubmit={(e) => handleSubmit(e)}> */}
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="campaignname"
                                            name="campaignname"
                                            type="text"
                                            label="Campaign Name"
                                            placeholder='Enter Campaign Name'
                                            value={formData.campaignname}
                                            maxLength={PROMOTION_NAME_MAX_LENGTH}
                                            errorMessage={errors.campaignname}
                                            isMandatory
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWTextarea
                                            id="campaigndetails"
                                            name="campaigndetails"
                                            type="text"
                                            label="Campaign Description"
                                            placeholder='Enter Campaign Description'
                                            errorMessage={errors.campaigndetails}
                                            maxLength={PROMOTION_DETAIL_MAX_LENGTH}
                                            onChange={handleInputChange}
                                            value={formData.campaigndetails}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="assignedlocation"
                                            name="assignedlocation"
                                            options={locations}
                                            label="Assign Location"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Assign Location"
                                            multi={false}
                                            disabled={props.edit ? true : false}
                                            values={locations.filter(option => option.value === formData.assignedlocation)}
                                            errorMessage={errors.assignedlocation}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'assignedlocation')}
                                            isMandatory
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <div className="adderss-sec assign-user-sec">
                                            <label>Assign User Type</label>
                                            <div className="radio-field-group d-flex align-items-center">
                                                <CWRadio
                                                    id="signedUser"
                                                    name="userType"
                                                    label="Users of PWA"
                                                    checked={isSignedUpUser}
                                                    onChange={() => handleUserTypeChange(true)}
                                                    disabled={props.edit ? true : false}
                                                />
                                                <CWRadio
                                                    id="unsignedUser"
                                                    name="userType"
                                                    label="Non-Registered Users"
                                                    checked={!isSignedUpUser}
                                                    onChange={() => handleUserTypeChange(false)}
                                                    disabled={props.edit ? true : false}
                                                />

                                            </div>
                                        </div>

                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        {(isSignedUpUser == true) && (<CWSelect
                                            id="userSubscription"
                                            name="userSubscription"
                                            options={userSubscriptions}
                                            label="Target Subscription Users"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Target Subscription Users"
                                            multi={false}
                                            values={userSubscriptions && userSubscriptions.filter(option => option.value === formData.userSubscription)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'userSubscription')}
                                            errorMessage={errors.userSubscription}
                                            disabled={props.edit ? true : false}
                                            isMandatory
                                        />)}
                                    </Col>
                                    <Col md={6}>

                                    </Col>

                                </Row>
                                <Row>
                                    <Col md={6}> 
                                        <CWSelect
                                            id="subscriptionType"
                                            name="subscriptionType"
                                            options={SubscriptionTypeOptions}
                                            label="Assign Company Subscription Type"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Assign Company Subscription Type"
                                            multi={false}
                                            values={SubscriptionTypeOptions && SubscriptionTypeOptions.filter(option => option.value === formData.subscriptionType)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'subscriptionType')}
                                            errorMessage={errors.subscriptionType}
                                            disabled={props.edit ? true : false}
                                            isMandatory
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWSelect
                                            id="washname"
                                            name="washname"
                                            options={washNames && washNames.filter(option => option.value != formData.userSubscription)}
                                            label="Select Wash"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Wash"
                                            multi={false}
                                            values={washNames && washNames.filter(option => option.value === formData.washname)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'washname')}
                                            errorMessage={errors.washname}
                                            disabled={props.edit ? true : false}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="discounttype"
                                            name="discounttype"
                                            options={discountOptions}
                                            label="Discount Type"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Discount Type"
                                            errorMessage={errors.discounttype}
                                            multi={false}
                                            disabled={props.edit ? true : false}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'discounttype')}
                                            className="no-input"
                                            values={discountOptions.filter(option => option.value === formData.discounttype)}
                                            isMandatory
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInputGroup
                                            id="discount_value"
                                            name="discount_value"
                                            type="number"
                                            label="Discount Value"
                                            isMandatory
                                            placeholder={(formData.discounttype == 1) ? '$' : (formData.discounttype == 2) ? '%' : "Enter Discount Value"}
                                            readOnly={props.edit}
                                            maxLength={DISCOUNT_MAX_LENGTH}
                                            value={formData.discount_value}
                                            errorMessage={errors.discount_value}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="duration"
                                            name="CampaignDuration"
                                            options={durationOpions}
                                            label="Duration"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Duration"
                                            multi={false}
                                            isMandatory
                                            disabled={props.edit || formData.subscriptionType === 1 ? true : false}
                                            className="no-input"
                                            errorMessage={errors.CampaignDuration}
                                            values={
                                                formData.subscriptionType === 1 
                                                    ? durationOpions.filter(option => option.value === "1") 
                                                    : durationOpions.filter(option => option.value == formData.CampaignDuration)
                                            }
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'CampaignDuration')}
                                        />
                                        <p className='promo-note mb-3'>Note: {CommonMessages.campaign_duration_note} </p>

                                    </Col>
                                    <Col md={3}>
                                        <div className="form-group date-space">
                                            <p className="label">Valid From<span className="mandatory">*</span></p>
                                            <DatePicker
                                                showIcon
                                                selected={startDate}
                                                onChange={handleDateChange(setStartDate, 'validFrom')}
                                                placeholderText='MM/DD/YYYY'
                                                id='validFrom'
                                                name='validFrom'
                                                value={formData.validFrom}
                                            />
                                            {errors.validFrom && <span className="error-msg">{errors.validFrom}</span>}

                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="form-group date-space">

                                            <p className="label">Valid To<span className="mandatory">*</span></p>
                                            <DatePicker
                                                showIcon
                                                selected={endDate}
                                                onChange={handleDateChange(setEndDate, 'validTo')}
                                                placeholderText='MM/DD/YYYY'
                                                id='validTo'
                                                name='validTo'
                                                value={formData.validTo}
                                                minDate={startDate || undefined}
                                            />
                                            {errors.validTo && <span className="error-msg">{errors.validTo}</span>}

                                        </div>
                                    </Col>
                                </Row>
                                
                                {formData.userType == 0 ? 
                                <div className='mt-4'>
                                <CWCheckbox
                                    id="multipleCodeGeneration"
                                    name="multipleCodeGeneration"
                                    label="Generate multiple codes?"
                                    checked={formData.multipleCodeGeneration}
                                    disabled={props.edit ? true : false}
                                    onChange={(event) => handleMultipleCodeCheck(event.target.checked)}
                                />
                                </div>
                                :""
                                }
                                 {formData.multipleCodeGeneration == true ? <Col md={6}>
                              <CWInputGroup
                                 id="noOfCodes"
                                 name="noOfCodes"
                                 type="number"
                                 label="Number of Codes to be generated"
                                 isMandatory
                                 placeholder="Enter number of codes to be generated"
                                 readOnly={props.edit}
                                 maxLength={4}
                                 value={formData.noOfCodes}
                                 errorMessage={errors.noOfCodes}
                                 onChange={handleInputChange}
                             /> 
                             <p>Codes will be generated in the background on adding this campaign.</p>
                         </Col> : <Row>
                                    <Col md={6}>
                                        <div className="promo-group position-relative">
                                            <CWInput
                                                id="generatecode"
                                                name="generatecode"
                                                type="text"
                                                label="Generate Campaign Code"
                                                value={formData.generatecode}
                                                maxLength={PROMO_CODE_MAX_LENGTH}
                                                errorMessage={errors.generatecode}
                                                onChange={handleInputChange}
                                                readOnly
                                                isMandatory
                                            />
                                            {!props.edit ? <Button title="Generate Code" variant="outline-primary" className="outline-btn f-medium" onClick={() => generateCode()}>Generate Code</Button> : ""}
                                        </div>
                                    </Col>
                                </Row>}
                                
                                

                            </div>
                        </div>
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.manage_campaigns}/${params.id}`) : navigate(`/${pageURLs.manage_campaigns}`)}>Cancel</Button>
                            <Button title="Review" variant="primary" className="primary-btn" type="button" onClick={showReviewpopupModal}>Review</Button>
                        </div>
                    {/* </form> */}
                </div>
            </Container >
            <Modal
                show={showReviewpopup}
                onHide={hideReviewpopupModal}
                centered
                className='campaign-modal'
            >
                <Modal.Body>
                    <h4 className='f-bold'> Review</h4>
                    <div className="campaign-content d-grid">
                         <div className="campaign-info">
                            <p>Campaign Name</p>
                            <h6>{formData.campaignname}</h6>
                         </div>
                         <div className="campaign-info">
                            <p>Campaign Description</p>
                            <h6>{formData.campaigndetails}</h6>
                         </div>
                         <div className="campaign-info">
                            <p>Location</p>
                            <h6>{locations.find((option) => option.value === formData.assignedlocation)?.label || "No location selected"}</h6>
                         </div>
                         <div className="campaign-info">
                            <p>User type</p>
                            <h6>{formData.userType === 1 ? "Users of PWA" : "Non-Registered Users"}</h6>
                         </div> 
                         {(isSignedUpUser == true) && (
                         <div className="campaign-info">
                            <p>Target Subscription Users</p>
                            <h6>{userSubscriptions.find((option) => option.value === formData.userSubscription)?.label ||  "No Target Subscription Users selected"}</h6>
                         </div>)}
                         <div className="campaign-info">
                            <p>Company Subscription Type</p>
                            <h6>{SubscriptionTypeOptions.find((option) => option.value === formData.subscriptionType)?.label || "No subscription selected"}</h6>
                         </div>  
                         <div className="campaign-info">
                            <p>Wash</p>
                            <h6>{washNames.find((option) => option.value === formData.washname)?.label || "No wash selected"}</h6>
                         </div>
                         <div className="campaign-info">
                            <p>Discount Type</p>
                            <h6>{discountOptions.find((option) => option.value === formData.discounttype)?.label || "No discount value selected"}</h6>
                         </div>  
                         <div className="campaign-info">
                            <p>Discount Value </p>
                            <h6>{formData.discount_value}</h6>
                         </div>
                         <div className="campaign-info">
                            <p>Duration</p>
                            <h6>
                                {
                                formData.subscriptionType === 1 
                                    ? durationOpions.find((option) => option.value === "1")?.label 
                                    : durationOpions.find((option) => option.value == formData.CampaignDuration)?.label
                                }
                            </h6>
                         </div>
                         <div className="campaign-info">
                            <p>Valid From</p>
                            <h6>{moment(formData.validFrom).format('MM/DD/YYYY')}</h6>
                         </div> 
                         <div className="campaign-info">
                            <p>Valid To</p>
                            <h6>{moment(formData.validTo).format('MM/DD/YYYY')}</h6>
                         </div>  
                         {formData.multipleCodeGeneration === false ? <div className="campaign-info">
                            <p>Campaign Code</p>
                            <h6>{formData.generatecode}</h6>
                         </div>:""  }                     
                    </div>
                    <div className="btn-sec d-flex justify-content-end">
                        <Button title="Edit" variant="light" className="secondary-btn" onClick={hideReviewpopupModal}>Edit</Button>
                        <Button title="Save" variant="primary" className='primary-btn btn-main m-0' onClick={handleSubmit}>Save</Button>
                    </div>
                </Modal.Body>
            </Modal>
            {loading && <SmallLoader />}

        </div >
    )
}

export default AddCampaign;
